import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Select from 'react-tailwindcss-select';

import { useInsideDialog } from '@/components/Ui/Dialog';

import { PortalSelect } from './Select';

/**
 * Detects iOS / iPadOS (multi-touch Mac).
 */
function isIOS(): boolean {
    if (typeof navigator === 'undefined') {
        return false;
    }
    return (
        /iPad|iPhone|iPod/.test(navigator.userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
}

type OptionValue = string | number | boolean;

interface Option {
    value: OptionValue;
    label: string;
}

interface OptionGroup {
    label: string;
    options: Option[];
}

interface StringOption {
    value: string;
    label: string;
}

interface StringOptionGroup {
    label: string;
    options: StringOption[];
}

interface Props {
    name: string;
    options: Array<Option | OptionGroup>;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    isMultiple?: boolean;
    isClearable?: boolean;
    onChange?: (value: OptionValue | OptionValue[] | null) => void;

    /**
     * Render the dropdown menu into a `document.body` portal (see
     * `Select`'s `usePortal`), so it isn't clipped by a dialog's
     * `overflow-hidden`/`overflow-y-auto` ancestors. Defaults to
     * automatic: on inside a `Dialog` (detected via context), off on
     * regular pages. Ignored on iOS, where the native picker is used.
     */
    usePortal?: boolean;
}

/**
 * RHF-compatible analog of `Ui/Form/AdaptiveSelect.jsx`. On iOS uses a native
 * `<select>` so the system picker wheel appears; on other platforms uses
 * `react-tailwindcss-select`. Wires the field via RHF's `<Controller>` so the
 * value↔option mapping is handled internally.
 *
 * `react-tailwindcss-select` only accepts string option values, but callers
 * (e.g. yes/no toggles) legitimately store booleans or numbers in the form.
 * Options are stringified only for the underlying widget; the resolved
 * selection is mapped back to its original value before reaching RHF.
 */
export function AdaptiveSelect({
    name,
    options,
    placeholder = 'Select…',
    disabled = false,
    className = '',
    isMultiple,
    isClearable,
    onChange,
    usePortal,
}: Props) {
    const { control } = useFormContext();
    const insideDialog = useInsideDialog();
    const shouldPortal = usePortal ?? insideDialog;
    const ios = useMemo(() => {
        return isIOS();
    }, []);

    return (
        <Controller
            control={ control }
            name={ name }
            render={ ({ field }) => {
                const flatOptions = options.flatMap((o) => {
                    return 'value' in o ? [
                        o,
                    ] : o.options;
                });

                const toOriginalValue = (stringValue: string): OptionValue => {
                    const match = flatOptions.find((o) => {
                        return String(o.value) === stringValue;
                    });

                    return match ? match.value : stringValue;
                };

                if (ios && !isMultiple) {
                    return (
                        <select
                            className={ `flex items-center h-10 px-3 py-2 text-base ring-offset-background focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${ className }` }
                            disabled={ disabled }
                            onChange={ (e) => {
                                const value = toOriginalValue(e.target.value);

                                field.onChange(value);
                                onChange?.(value);
                            } }
                            value={ field.value === null || field.value === undefined ? '' : String(field.value) }
                        >
                            { placeholder && (
                                <option value=''>{ placeholder }</option>
                            ) }
                            { flatOptions.map((opt) => {
                                return (
                                    <option key={ String(opt.value) } value={ String(opt.value) }>
                                        { opt.label }
                                    </option>
                                );
                            }) }
                        </select>
                    );
                }

                // react-tailwindcss-select only accepts string option values — stringify for its sake.
                const stringOptions: Array<StringOption | StringOptionGroup> = options.map((o) => {
                    if ('value' in o) {
                        return { value: String(o.value), label: o.label };
                    }

                    return { label: o.label,
                        options: o.options.map((opt) => {
                            return { value: String(opt.value), label: opt.label };
                        }) };
                });

                const stringFlatOptions = stringOptions.flatMap((o) => {
                    return 'value' in o ? [
                        o,
                    ] : o.options;
                });

                const matches = stringFlatOptions.filter((o) => {
                    if (Array.isArray(field.value)) {
                        return (field.value as OptionValue[]).map(String).includes(o.value);
                    }

                    return String(field.value) === o.value;
                });

                if (shouldPortal) {
                    return (
                        <PortalSelect
                            className={ className }
                            isClearable={ isClearable }
                            isDisabled={ disabled }
                            isMultiple={ isMultiple }
                            matches={ matches }
                            onBlurField={ field.onBlur }
                            onClear={ () => {
                                const empty = isMultiple ? [] : null;

                                field.onChange(empty);
                                onChange?.(empty);
                            } }
                            onSelect={ (option) => {
                                if (isMultiple) {
                                    const current = Array.isArray(field.value) ? field.value as OptionValue[] : [];
                                    const next = current.some((v) => {
                                        return String(v) === option.value;
                                    })
                                        ? current.filter((v) => {
                                            return String(v) !== option.value;
                                        })
                                        : [
                                            ...current,
                                            toOriginalValue(option.value),
                                        ];

                                    field.onChange(next);
                                    onChange?.(next);
                                    return;
                                }

                                const value = toOriginalValue(option.value);

                                field.onChange(value);
                                onChange?.(value);
                            } }
                            options={ stringOptions }
                            placeholder={ placeholder }
                        />
                    );
                }

                let componentValue;

                if (matches.length === 0) {
                    componentValue = null;
                } else if (isMultiple) {
                    componentValue = matches;
                } else {
                    componentValue = matches[0];
                }

                const disabledClasses = disabled ? 'opacity-50' : '';

                return (
                    <div className='grow'>
                        <Select
                            classNames={ {
                                menuButton: () => {
                                    return `w-full rounded-md flex text-sm text-muted-foreground border border-input rounded-md shadow-sm transition-all duration-300 focus:outline-none hover:border-ring focus:border-info focus:ring focus:ring-info/20 bg-background ${ className } ${ disabledClasses }`;
                                },
                                list: 'bg-background max-h-72 overflow-y-auto',
                                menu: 'absolute z-10 w-full bg-background shadow-lg border border-border rounded py-1 mt-1.5 text-sm text-foreground',
                                tagItem: (args) => {
                                    return `relative flex items-center rounded-md text-xs font-medium bg-muted text-foreground ${ args?.isDisabled ? 'opacity-50' : '' }`;
                                },
                                tagItemText: 'pl-2 pr-6 py-1 pointer-events-none',
                                tagItemIconContainer: 'absolute inset-0 flex items-center justify-end pr-1.5 cursor-pointer rounded-md hover:bg-destructive/10 hover:text-destructive dark:hover:bg-destructive/20 dark:hover:text-destructive',
                            } }
                            isClearable={ isClearable }
                            isDisabled={ disabled }
                            isMultiple={ isMultiple }
                            onChange={ (val) => {
                                if (!val) {
                                    const empty = isMultiple ? [] : null;

                                    field.onChange(empty);
                                    onChange?.(empty);
                                    return;
                                }
                                if (Array.isArray(val)) {
                                    const values = val.map((o) => {
                                        return toOriginalValue(o.value);
                                    });

                                    field.onChange(values);
                                    onChange?.(values);
                                } else {
                                    const value = toOriginalValue(val.value);

                                    field.onChange(value);
                                    onChange?.(value);
                                }
                            } }
                            options={ stringOptions }
                            placeholder={ placeholder }
                            primaryColor=''
                            value={ componentValue }
                        />
                    </div>
                );
            } }
        />
    );
}
